// Pagos de un cobro ya registrado en el vocabulario de las pantallas de lectura. Puro.
import type { components } from '@/api/services/retail-wizard/retail-wizard.gen';

type SalePaymentWire = components['schemas']['SalePayment'];

/** `OTHER`: un medio que esta pantalla no conoce; se nombra neutro, nunca como efectivo. */
export type PaymentLineMethod = 'CASH' | 'CARD' | 'SPEI' | 'OTHER';

/** Un pago cobrado, con la transferencia como `SPEI` y el comprobante ausente como `null`. */
export interface PaymentLine {
    method: PaymentLineMethod;
    amountCents: number;
    cashConfirmedInRegister: boolean | null;
    cardLast4: string | null;
    authCode: string | null;
    speiTrackingKey: string | null;
    senderClabe: string | null;
}

/** Cobro de $0: no lleva medio ni pagos, y no se nombra como uno. */
export const NO_CHARGE_LABEL = 'Sin cobro';

/** Un cobro de $0 sin medio registrado. */
export function isNoCharge(method: string | null | undefined, amountCents: number): boolean {
    return method == null && amountCents === 0;
}

export const PAYMENT_LINE_LABEL: Record<PaymentLineMethod, string> = {
    CASH: 'Efectivo',
    CARD: 'Tarjeta',
    SPEI: 'Transferencia',
    OTHER: 'Otro medio',
};

/** Medio del wire o de pantalla (`WIRE` o `SPEI`) al vocabulario de lectura. */
export function toPaymentLineMethod(method: string): PaymentLineMethod {
    if (method === 'CARD') return 'CARD';
    if (method === 'WIRE' || method === 'SPEI') return 'SPEI';
    return method === 'CASH' ? 'CASH' : 'OTHER';
}

/** Pagos del wire; `null` si el registro es anterior al campo o no trae ninguno. */
export function toPaymentLines(
    payments: readonly SalePaymentWire[] | null | undefined,
): PaymentLine[] | null {
    if (!payments || payments.length === 0) return null;
    return payments.map((p) => ({
        method: toPaymentLineMethod(p.method),
        amountCents: p.amountCents,
        cashConfirmedInRegister: p.cashConfirmedInRegister ?? null,
        cardLast4: p.cardLast4 ?? null,
        authCode: p.authCode ?? null,
        speiTrackingKey: p.speiTrackingKey ?? null,
        senderClabe: p.senderClabe ?? null,
    }));
}

/** Comprobante del pago: «•• 4821 · Aut. 553201», «Confirmado en caja» o «Sin comprobante». */
export function paymentReceiptLabel(line: PaymentLine): string {
    if (line.method === 'OTHER') return 'Sin comprobante';
    if (line.method === 'CASH') {
        return line.cashConfirmedInRegister ? 'Confirmado en caja' : 'Sin confirmar en caja';
    }
    const parts =
        line.method === 'CARD'
            ? [
                  line.cardLast4 ? `•• ${line.cardLast4}` : null,
                  line.authCode ? `Aut. ${line.authCode}` : null,
              ]
            : [
                  line.speiTrackingKey ? `Rastreo ${line.speiTrackingKey}` : null,
                  line.senderClabe ? `CLABE •• ${line.senderClabe.slice(-4)}` : null,
              ];
    const shown = parts.filter((p): p is string => p !== null);
    return shown.length > 0 ? shown.join(' · ') : 'Sin comprobante';
}

/** Medios del cobro en orden de aparición: «Tarjeta + Efectivo», «Tarjeta ×2». */
export function paymentMethodsLabel(lines: readonly PaymentLine[]): string {
    const counts = new Map<PaymentLineMethod, number>();
    for (const line of lines) counts.set(line.method, (counts.get(line.method) ?? 0) + 1);
    return [...counts]
        .map(([method, n]) => `${PAYMENT_LINE_LABEL[method]}${n > 1 ? ` ×${String(n)}` : ''}`)
        .join(' + ');
}

/** Etiqueta única de un medio, venga en el vocabulario del wire o de pantalla. */
export function paymentMethodName(method: string): string {
    return PAYMENT_LINE_LABEL[toPaymentLineMethod(method)];
}

export function paymentLinesTotalCents(lines: readonly PaymentLine[]): number {
    return lines.reduce((sum, l) => sum + l.amountCents, 0);
}
